import React from "react";
import { Button, Tooltip } from "@mui/material";

const MIN_SCALE = 0.4;
const MAX_SCALE = 1;

/**
 * Mimics a browser "zoom" on mobile for full-height (80vh) DataGrids.
 *
 * Because CSS transforms don't reflow layout, the grid is scaled down and its
 * layout size is grown so the scaled result fills the container. Wrap the grid
 * in a `<Box sx={wrapperSx}>` so the oversized layout box is clipped and no
 * empty space remains.
 *
 * Usage:
 *   const { gridSx, wrapperSx, zoomButtons } = useMobileZoom(props.isDesktop);
 *   // in the toolbar, render {zoomButtons} next to the export button
 *   // wrap the grid:  <Box sx={wrapperSx}><StripedDataGrid sx={{ ...gridSx }} /></Box>
 */
export function useMobileZoom(isDesktop: boolean, defaultScale = 0.7) {
  const [scale, setScale] = React.useState(defaultScale);

  const decrease = () =>
    setScale((s) => Math.max(MIN_SCALE, Math.round((s - 0.1) * 10) / 10));
  const increase = () =>
    setScale((s) => Math.min(MAX_SCALE, Math.round((s + 0.1) * 10) / 10));

  const gridSx: Record<string, any> = isDesktop
    ? {}
    : {
        height: `${80 / scale}vh`,
        width: `${100 / scale}%`,
        transform: `scale(${scale})`,
        transformOrigin: "top left",
      };

  const wrapperSx: Record<string, any> = isDesktop
    ? {}
    : { height: "80vh", overflow: "hidden" };

  const zoomButtons = isDesktop ? null : (
    <>
      <Tooltip title="Zoom out">
        <Button size="small" onClick={decrease} sx={{ minWidth: 32 }}>
          −
        </Button>
      </Tooltip>
      <Tooltip title="Zoom in">
        <Button size="small" onClick={increase} sx={{ minWidth: 32 }}>
          +
        </Button>
      </Tooltip>
    </>
  );

  return { scale, gridSx, wrapperSx, zoomButtons };
}
